import { MonitorCheck } from '@orgus/ui/icons';
import { useState } from 'react';
import type { Id, LandingConsentWait, LandingQueue } from '@orgus/protocol';
import { Button, RelativeTime, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { CeilingHint } from '../trust/CeilingHint.tsx';
import { askLandingConsent } from './api.ts';
import { Notice } from './Notice.tsx';

/**
 * Landing consent (R-FN-8): a machine waits for its person to allow landing for this project.
 * Orgus asked on that computer; the one action asks again. Only someone there can say yes, and
 * the yes covers this project's landing work only. A computer with no screen gets the one
 * command to type there.
 */
const LINE: Record<LandingConsentWait['state'], (p: string) => string> = {
  asking: (p) => `Orgus asked on that computer. Someone there clicks Allow, and it allows landing for ${p} only.`,
  declined: () => 'Nobody there said yes yet.',
  'no-dialog': () => 'That computer has no screen to ask on.',
  offline: () => 'That computer did not answer.',
};

/** The plain line under a waiting machine's title. */
export function consentLine(c: LandingConsentWait, projectName: string): string {
  return LINE[c.state](projectName);
}

/** For a machine with no screen: the one command to type there, or null. */
export function consentCommand(c: LandingConsentWait): string | null {
  return c.state === 'no-dialog' && c.next ? c.next.replace(/^On that computer run:\s*/, '') : null;
}

export function ConsentNotice({ queue, projectName }: { queue: LandingQueue; projectName: string }) {
  const [busy, setBusy] = useState<Id | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const ask = async (machineId: Id) => {
    setBusy(machineId);
    setError(null);
    try {
      await askLandingConsent(queue.id, machineId);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  };
  // Beside the words on wide screens, under them on a phone.
  const button = (c: LandingConsentWait, className?: string) => (
    <Button size="sm" variant="secondary" className={className} icon={<MonitorCheck />} loading={busy === c.machineId} onClick={() => void ask(c.machineId)}>
      {`Allow landing on ${c.machineName}`}
    </Button>
  );
  return (
    <div className="flex flex-col gap-2" data-landing-consent="">
      {(queue.consent ?? []).map((c) => {
        const command = consentCommand(c);
        return (
          <Notice
            key={c.machineId}
            title={`Landing waits for a yes on ${c.machineName}.`}
            action={button(c, 'hidden shrink-0 sm:inline-flex')}
          >
            {consentLine(c, projectName)} {c.state === 'asking' ? <RelativeTime ts={c.askedAt} prefix="Asked" /> : c.state !== 'no-dialog' && c.next ? c.next : null}
            <span className="mt-2 flex sm:hidden">{button(c)}</span>
            {command && <CeilingHint className="mt-2" lead="Or, on that computer, run once:" command={command} />}
          </Notice>
        );
      })}
      {error && (
        <p role="alert" className="text-caption text-status-danger">
          {`${error.what} ${error.why} ${error.next}`}
        </p>
      )}
    </div>
  );
}
